import { useState } from 'react';
import classNames from 'classnames';
import styles from './LanguageSelector.module.scss';
import { UI_Icon } from 'ui/components/1-atoms/Media/Icon/Icon';
import ChevronUp from 'ui/icons/icon-chevron-right-small.svg';
import { UI_Link } from 'ui/components/1-atoms/Action/Link/Link';
import { useRouteContext } from 'ui/contexts/RouteContext';

export interface UI_LanguageSelectorProps {
	className?: string;

	languages: {
		name: string;
		locale: string;
		path: string;
	}[];

	currentLanguage: {
		name: string;
		locale: string;
		path: string;
	};
}

export const UI_LanguageSelector: React.FC<UI_LanguageSelectorProps> = ({ className, languages, currentLanguage }) => {
	const { routeHandler } = useRouteContext();

	const [isOpen, setIsOpen] = useState<boolean>(false);

	const toggleOpen = () => {
		setIsOpen(!isOpen);
	};

	const onEnterEvent = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			toggleOpen();
		}
	};

	const findLocale = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const chosenLanguage = languages.find((language) => e.target.value === language.locale);

		return chosenLanguage?.locale;
	};

	return (
		languages.length > 1 && (
			<div className={classNames(styles.LanguageSelector, className)}>
				{/* DESKTOP */}
				<div className={styles.LanguageSelector_list}>
					<div
						tabIndex={0}
						className={classNames(styles.LanguageSelector_toggle, {
							[styles.isOpen]: isOpen,
						})}
						aria-expanded={isOpen}
						onClick={toggleOpen}
						onKeyDown={onEnterEvent}
					>
						<p>{currentLanguage.name}</p>

						<div className={styles.LanguageSelector_icon}>
							<UI_Icon size="md">
								<ChevronUp />
							</UI_Icon>
						</div>
					</div>

					<ul
						role="navigation"
						className={classNames(styles.LanguageSelector_dropdown, {
							[styles.isOpen]: isOpen,
						})}
					>
						{languages.map((language, index) => {
							return (
								language.locale !== currentLanguage.locale && (
									<li key={index} onClick={toggleOpen}>
										<UI_Link
											className={classNames(styles.LanguageSelector_item, styles.LanguageSelector_link)}
											href="/"
											locale={language.locale}
										>
											{language.name}
										</UI_Link>
									</li>
								)
							);
						})}
					</ul>
				</div>

				{/* MOBILE */}
				<select
					role="navigation"
					className={styles.LanguageSelector_select}
					onChange={(e) => {
						const locale = findLocale(e);

						if (locale) {
							routeHandler(e, '/', locale);
						}
					}}
				>
					<option value={currentLanguage.locale} hidden>
						{currentLanguage.name}
					</option>

					{languages.map((language, index) => {
						return (
							language.locale !== currentLanguage.locale && (
								<option key={index} value={language.locale}>
									{language.name}
								</option>
							)
						);
					})}
				</select>
			</div>
		)
	);
};
